import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { GISMap } from "@/components/GISMap";
import { Search, Filter, AlertOctagon, Check, Pickaxe } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["active","pending","conflict_review","expired","suspended"];
const COMMODITIES = ["Gold","Platinum","Lithium","Diamond","Nickel","Chrome","Copper","Coal","Iron","Tantalite"];

const statusChip = (s) => {
  const map = {
    active: "cad-chip-green",
    pending: "cad-chip-blue",
    conflict_review: "cad-chip-red",
    expired: "cad-chip",
    suspended: "cad-chip-gold",
  };
  return <span className={`cad-chip ${map[s] || ""}`}>{(s || "unknown").replace(/_/g, " ")}</span>;
};

export const MiningCadastre = () => {
  const { user } = useAuth();
  const canApprove = ["mining_commissioner", "sovereign_admin"].includes(user?.role);
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [commodity, setCommodity] = useState("");
  const [selected, setSelected] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["minerals", q, status, commodity],
    queryFn: async () => (await api.get("/mining/rights", {
      params: { q: q || undefined, status: status || undefined, commodity: commodity || undefined, limit: 120 },
    })).data,
  });

  const approveMut = useMutation({
    mutationFn: async (id) => (await api.post(`/mining/rights/${id}/approve`)).data,
    onSuccess: (r) => {
      toast.success(`Licence ${r.licence_number} issued`);
      qc.invalidateQueries({ queryKey: ["minerals"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="page-mining">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">MiningCadastre360™</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Mineral Rights Register</h1>
          <div className="text-[13px] text-[#B3C2CC] mt-1">First-in-time priority · spatial conflict detection · immutable audit trail</div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <aside className="lg:col-span-1 space-y-3">
          <div className="cad-panel p-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5] mb-2 flex items-center gap-2">
              <Filter size={12} /> Filters
            </div>
            <div className="space-y-3">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-2.5 text-[#7C93A5]" />
                <input data-testid="mining-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Licence, applicant, ref"
                  className="w-full bg-[#071B2B] border cad-hairline rounded-sm pl-8 pr-2 py-2 text-[13px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] outline-none" />
              </div>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="mining-status"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
              <select value={commodity} onChange={(e) => setCommodity(e.target.value)} data-testid="mining-commodity"
                      className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                <option value="">All commodities</option>
                {COMMODITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {selected && (
            <div className="cad-panel p-4" data-testid="mineral-detail">
              <div className="flex items-center justify-between">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Selected Right</div>
                {statusChip(selected.status)}
              </div>
              <div className="font-display text-base font-bold text-[#F6F8FA] mt-1 truncate">
                {selected.licence_number || selected.application_number}
              </div>
              <dl className="mt-2 text-[12px] space-y-1.5 text-[#B3C2CC]">
                <div className="flex justify-between"><dt>Commodity</dt><dd className="text-[#F6F8FA]">{selected.commodity}</dd></div>
                <div className="flex justify-between"><dt>Applicant</dt><dd className="text-right max-w-[60%] text-[#F6F8FA]">{selected.applicant}</dd></div>
                <div className="flex justify-between"><dt>Province</dt><dd className="text-[#F6F8FA]">{selected.province}</dd></div>
                <div className="flex justify-between"><dt>Area</dt><dd className="font-mono-cad text-[#F6F8FA]">{selected.area_ha} ha</dd></div>
                <div className="flex justify-between"><dt>Compliance</dt><dd className="font-mono-cad text-[#F6F8FA]">{selected.compliance_score ?? "—"}</dd></div>
                <div className="flex justify-between"><dt>Expiry</dt><dd className="font-mono-cad text-[#F6F8FA]">{(selected.expiry_date || "").slice(0, 10)}</dd></div>
              </dl>
              {selected.conflicts?.length > 0 && (
                <div className="mt-3 p-2 border border-[#A93F3F]/50 rounded-sm bg-[#A93F3F]/10">
                  <div className="text-[11px] uppercase tracking-widest text-[#E67E7E] flex items-center gap-1.5"><AlertOctagon size={12} /> Spatial conflict</div>
                  <div className="text-[12px] text-[#DCE3E8] mt-1">{selected.conflicts.length} overlap(s) detected in bounding-box scan.</div>
                </div>
              )}
              {canApprove && selected.status === "pending" && (
                <button data-testid={`approve-${selected.id}`}
                        disabled={approveMut.isPending}
                        onClick={() => approveMut.mutate(selected.id)}
                        className="cad-btn-primary w-full mt-3 py-2 text-[12px] rounded-sm inline-flex items-center justify-center gap-1.5">
                  <Check size={13} /> Approve & issue licence
                </button>
              )}
              <a href={`/verify/${selected.licence_number || selected.application_number}`}
                 target="_blank" rel="noopener noreferrer" data-testid="mineral-verify"
                 className="cad-btn-outline block text-center mt-2 py-2 text-[12px] rounded-sm">
                Verify certificate
              </a>
            </div>
          )}
        </aside>

        <section className="lg:col-span-3 space-y-4">
          <GISMap minerals={items} height={420} onFeatureClick={(_, f) => setSelected(f)} />
          <div className="cad-panel overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b cad-hairline">
                  {["Reference","Commodity","Applicant","Province","Type","Area","Status","Expiry"].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#7C93A5]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="cad-tbody">
                {isLoading && <tr><td colSpan={8} className="px-3 py-6 text-center text-[#7C93A5]">Loading…</td></tr>}
                {items.map((m) => (
                  <tr key={m.id} onClick={() => setSelected(m)} data-testid={`mineral-row-${m.id}`} className="cursor-pointer">
                    <td className="px-3 py-2 font-mono-cad text-[#F6F8FA]">{m.licence_number || m.application_number}</td>
                    <td className="px-3 py-2 flex items-center gap-1.5"><Pickaxe size={12} className="text-[#C99A2E]" /> {m.commodity}</td>
                    <td className="px-3 py-2 text-[#DCE3E8]">{m.applicant}</td>
                    <td className="px-3 py-2">{m.province}</td>
                    <td className="px-3 py-2 capitalize text-[#B3C2CC]">{(m.licence_type || "").replace(/_/g, " ")}</td>
                    <td className="px-3 py-2 font-mono-cad">{m.area_ha}</td>
                    <td className="px-3 py-2">{statusChip(m.status)}</td>
                    <td className="px-3 py-2 font-mono-cad text-[#B3C2CC]">{(m.expiry_date || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};
